import axiosInstance from 'service/axiosInstance';
import {
  HrmNotification,
  HrmNotificationListResponse,
  HrmNotificationFilters,
} from 'types/notifications';

type HrmListPage = {
  data: HrmNotification[];
  totalElements: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
};

// ─── List ─────────────────────────────────────────────────────────────────────

export const getHrmNotifications = async (
  filters?: HrmNotificationFilters,
): Promise<HrmListPage> => {
  const response = await axiosInstance.get<HrmNotificationListResponse>(
    'hrm/notifications',
    { params: filters },
  );
  const responseData = response.data.response as any;
  const list = responseData.data || responseData.content || [];
  if (Array.isArray(list)) {
    responseData.data = list.map((n: any) => ({
      ...n,
      isRead: n.isRead ?? n.read ?? false,
      createdAt: n.createdAt ?? n.createdDate ?? n.timestamp ?? new Date().toISOString(),
    }));
  }
  return responseData;
};

// ─── Unread Count ─────────────────────────────────────────────────────────────

export const getHrmUnreadCount = async (): Promise<number> => {
  const response = await axiosInstance.get<{ response: { unreadCount: number } }>(
    'hrm/notifications/unread-count',
  );
  return response.data.response.unreadCount;
};

// ─── Stats ────────────────────────────────────────────────────────────────────

export const getHrmNotificationStats = async (): Promise<{ total: number; unread: number; read: number }> => {
  const response = await axiosInstance.get<{ response: { total: number; unread: number; read: number } }>(
    'hrm/notifications/stats',
  );
  return response.data.response;
};

// ─── Mark Single as Read ───────────────────────────────────────────────────────

export const markHrmNotificationAsRead = async (uuid: string): Promise<void> => {
  await axiosInstance.put(`hrm/notifications/${uuid}/read`);
};

// ─── Mark All as Read ─────────────────────────────────────────────────────────

export const markAllHrmNotificationsAsRead = async (): Promise<void> => {
  await axiosInstance.put('hrm/notifications/read-all');
};

// ─── Bulk Mark as Read ────────────────────────────────────────────────────────

export const bulkMarkHrmNotificationsAsRead = async (
  notificationUuidList: string[],
): Promise<void> => {
  await axiosInstance.put('hrm/notifications/bulk-read', { notificationUuidList });
};

// ─── Delete Single ────────────────────────────────────────────────────────────

export const deleteHrmNotification = async (uuid: string): Promise<void> => {
  await axiosInstance.delete(`hrm/notifications/${uuid}`);
};

// ─── Clear All ────────────────────────────────────────────────────────────────

export const clearAllHrmNotifications = async (): Promise<void> => {
  await axiosInstance.delete('hrm/notifications/clear-all');
};

// ─── Bulk Delete ──────────────────────────────────────────────────────────────

export const bulkDeleteHrmNotifications = async (
  notificationUuidList: string[],
): Promise<void> => {
  await axiosInstance.delete('hrm/notifications/bulk-delete', {
    data: { notificationUuidList },
  });
};
